<template>
  <div :id="getProps.tableId" ref="wrapRef" :class="getWrapperClass">
    <BasicForm
      v-if="getBindValues.useSearchForm"
      ref="formRef"
      submitOnReset
      v-bind="getFormProps"
      :tableAction="tableAction"
      @register="registerForm"
      @submit="handleSearchInfoChange"
      @focus-change="handleFocusChange"
      @advanced-change="redoHeight"
    >
      <template v-for="item in getFormSlotKeys" #[replaceFormSlotKey(item)]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </BasicForm>

    <slot name="tableBefore"></slot>

    <div v-if="ifHideTable" class="ant-table-wrapper">
      <TableHeader>
        <template v-for="item in Object.keys($slots)" #[item]="data" :key="item">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </TableHeader>

      <div class="basic-main-area" :style="{ height: mainAreaHeight + 'px' }">
        <slot name="mainArea" v-bind="{ getBindValues, ...tableAction }"></slot>
      </div>
    </div>

    <Table
      v-if="!ifHideTable && isMounted"
      v-show="getEmptyDataIsShowTable"
      ref="tableElRef"
      v-bind="getBindValues"
      :rowClassName="getRowClassName"
      @change="handleTableChange"
      @resize-column="setColumnWidth"
      @expand="handleTableExpand"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data" :key="item">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
      <template #emptyText="data">
        <slot name="emptyText" v-bind="data || {}">
          <TableEmptyAdd
            :style="{
              height: getScrollRef?.y ? `${Number(getScrollRef?.y) - adaptiveSize(106)}px` : '',
            }"
          />
        </slot>
      </template>
      <template #headerCell="{ column }">
        <slot name="headerCell" v-bind="{ column }">
          <HeaderCell :column="column" :dataSource="getDataSourceRef" />
        </slot>
      </template>
      <template #bodyCell="data">
        <slot name="bodyCell" v-bind="data || {}"></slot>
      </template>
    </Table>

    <slot name="tableAfter"></slot>
  </div>
</template>

<script lang="ts" setup>
  import { useElementSize } from '@vueuse/core';
  import { Table } from 'ant-design-vue';
  import { debounce } from 'lodash-es';
  import {
    computed,
    inject,
    nextTick,
    ref,
    shallowRef,
    toRaw,
    unref,
    useAttrs,
    useSlots,
    useTemplateRef,
    watch,
  } from 'vue';
  import { useLoading } from '@mts-fe/hooks';
  import { getRowSpanCount, isFunction, omit } from '@mts-fe/utils';
  import { BasicForm, useForm } from '@mts/components/Form';
  import { PageWrapperFixedHeightKey } from '@mts/enums/pageEnum';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { adaptiveSize, mtsEmitter } from '@mts/utils';
  import HeaderCell from './components/HeaderCell.vue';
  import TableEmptyAdd from './components/TableEmptyAdd.vue';
  import TableHeader from './components/TableHeader.vue';
  import { useColumns } from './hooks/useColumns';
  import { useCustomRow } from './hooks/useCustomRow';
  import { useDataSource } from './hooks/useDataSource';
  import { usePagination } from './hooks/usePagination';
  import { useRowSelection } from './hooks/useRowSelection';
  import { useTableScrollTo } from './hooks/useScrollTo';
  import { useTableSortable } from './hooks/useSortable';
  import { createTableContext } from './hooks/useTableContext';
  import { useTableExpand } from './hooks/useTableExpand';
  import { useTableForm } from './hooks/useTableForm';
  import { useTableHeader } from './hooks/useTableHeader';
  import { useTableScroll } from './hooks/useTableScroll';
  import { useTableStyle } from './hooks/useTableStyle';
  import type {
    BasicColumn,
    BasicTableProps,
    ColumnChangeParam,
    SizeType,
    TableActionType,
  } from './types/table';
  import { InnerHandlers, InnerMethods } from './types/table';
  import { basicProps } from './props';

  defineOptions({ name: 'BasicTable' });

  const props = defineProps(basicProps);

  const emit = defineEmits([
    'fetch-success',
    'fetch-error',
    'fetch-done',
    'selection-change',
    'register',
    'row-click',
    'row-dbClick',
    'row-contextmenu',
    'row-mouseenter',
    'row-mouseleave',
    'expanded-rows-change',
    'change',
    'edit-change',
    'focus-change',
    'columns-change',
  ]);

  const attrs = useAttrs();
  const slots = useSlots();

  const tableElRef = useTemplateRef('tableElRef');
  const tableData = ref([]);

  const wrapRef = useTemplateRef('wrapRef');
  const formRef = useTemplateRef('formRef');
  const innerPropsRef = shallowRef<Partial<BasicTableProps>>();

  const mainAreaHeight = ref(0);
  const ifHideTable = computed(() => props.tableMode !== 'TABLE');
  watch(
    () => ifHideTable.value,
    () => {
      nextTick(() => {
        const offsetTop = (wrapRef.value as any)?.querySelector('.basic-main-area')?.offsetTop || 0;
        mainAreaHeight.value = height.value + 46 - offsetTop;
      });
    },
  );

  const { height } = useElementSize(wrapRef.value);

  const { prefixCls } = useDesign('basic-table');
  const [registerForm, formActions] = useForm();

  const getProps = computed(() => ({ ...props, ...unref(innerPropsRef) }) as BasicTableProps);

  const isFixedHeightPage = inject(PageWrapperFixedHeightKey, false);

  const { getLoading, setLoading } = useLoading(getProps);
  const { getPaginationInfo, getPagination, setPagination, setShowPagination, getShowPagination } =
    usePagination(getProps);

  const {
    getRowSelection,
    getRowSelectionRef,
    getSelectRows,
    setSelectedRows,
    clearSelectedRowKeys,
    getSelectRowKeys,
    deleteSelectRowByKey,
    setSelectedRowKeys,
  } = useRowSelection(getProps, tableData, emit);

  const {
    handleTableChange: onTableChange,
    getDataSourceRef,
    getDataSource,
    getRawDataSource,
    getSearchInfo,
    setTableData,
    updateTableDataRecord,
    deleteTableDataRecord,
    insertTableDataRecord,
    findTableDataRecord,
    fetch,
    getRowKey,
    reload,
    getAutoCreateKey,
    updateTableData,
    exchangeTableData,
  } = useDataSource(
    getProps,
    {
      tableData,
      getPaginationInfo,
      setLoading,
      setPagination,
      getFieldsValue: formActions.getFieldsValue,
      clearSelectedRowKeys,
    },
    emit,
  );

  useTableSortable(getProps, tableElRef, getDataSourceRef, exchangeTableData);

  function handleTableChange(pagination: any, filters: any, sorter: any, extra: any) {
    onTableChange(pagination, filters, sorter);
    emit('change', pagination, filters, sorter);
    // 解决通过useTable注册onChange时不起作用的问题
    const { onChange } = unref(getProps);
    onChange && isFunction(onChange) && onChange(pagination, filters, sorter, extra);
  }

  const isMounted = ref(props.isMounted);
  const {
    getViewColumns,
    getColumns,
    setCacheColumnsByField,
    setCacheColumns,
    setColumnWidth,
    setColumns,
    getCacheColumns,
    toggleActionColumn,
    runValidateFns,
  } = useColumns(getProps, getPaginationInfo, isMounted);

  const { getScrollRef, redoHeight } = useTableScroll(
    getProps,
    tableElRef,
    getViewColumns,
    getRowSelectionRef,
    getDataSourceRef,
    wrapRef,
    formRef,
  );
  const debounceRedoHeight = debounce(redoHeight, 50);

  const { scrollTo } = useTableScrollTo(tableElRef, getDataSourceRef, getRowKey);

  const { customRow } = useCustomRow(getProps, {
    setSelectedRowKeys,
    getSelectRowKeys,
    clearSelectedRowKeys,
    getAutoCreateKey,
    emit,
  });

  const { getRowClassName } = useTableStyle(getProps, prefixCls);

  const { getExpandOption, expandAll, expandRows, collapseRows, collapseAll, handleTableExpand } =
    useTableExpand(getProps, tableData, emit);

  const handlers: InnerHandlers = {
    onColumnsChange: (data: ColumnChangeParam[]) => {
      emit('columns-change', data);
      // support useTable
      unref(getProps).onColumnsChange?.(data);
    },
  };

  const methods: InnerMethods = {
    clearSelectedRowKeys,
    getSelectRowKeys,
    getDataSource,
  };

  const { getHeaderProps } = useTableHeader(getProps, slots, handlers, methods);

  const { getFormProps, replaceFormSlotKey, getFormSlotKeys, handleSearchInfoChange } =
    useTableForm(getProps, slots, fetch, getLoading);
  const handleFocusChange = (field?: string | false) => emit('focus-change', field);

  const getBindValues = computed(() => {
    const dataSource = unref(getDataSourceRef);
    const columns = toRaw(unref(getViewColumns));
    if (dataSource.length) {
      columns.forEach((column) => {
        if (column['mergeCellBy'] && !column.customCell) {
          const keys = [...column['mergeCellBy'].split(','), column.dataIndex!.toString()];
          column.customCell = (...args) => ({
            rowSpan: getRowSpanCount(dataSource, keys, args[1]),
          });
        }
        return column;
      });
    }
    let propsData: any = {
      ...attrs,
      customRow,
      ...unref(getProps),
      ...unref(getHeaderProps),
      scroll: unref(getScrollRef),
      loading: unref(getLoading),
      tableLayout: 'fixed',
      rowSelection: unref(getRowSelectionRef),
      rowKey: unref(getRowKey),
      columns,
      dataSource,
      pagination: toRaw(unref(getPaginationInfo)),
      ...unref(getExpandOption),
    };
    // if (slots.expandedRowRender) {
    //   propsData = omit(propsData, 'scroll');
    // }

    propsData = omit(propsData, ['class', 'onChange', 'onExpand']);
    return propsData;
  });

  const getWrapperClass = computed(() => {
    const values = unref(getBindValues);
    return [
      prefixCls,
      attrs.class,
      {
        [`${prefixCls}-form-container`]: values.useSearchForm,
        [`${prefixCls}--minimal`]: values.size === 'minimal',
        [`${prefixCls}--inset`]: values.inset,
      },
    ];
  });

  const getEmptyDataIsShowTable = computed(() => {
    const { emptyDataIsShowTable, useSearchForm } = unref(getProps);
    if (emptyDataIsShowTable || !useSearchForm) {
      return true;
    }
    return !!unref(getDataSourceRef).length;
  });

  watch(height, () => {
    unref(isFixedHeightPage) && props.canResize && debounceRedoHeight();
  });

  function setProps(props: Partial<BasicTableProps>, isInner?: boolean) {
    isInner && mtsEmitter.emit('tableInnerChanged', true);
    innerPropsRef.value = { ...unref(innerPropsRef), ...props };
    props.loading && setLoading(props.loading);

    if (!isInner && innerPropsRef.value.columns?.length) {
      nextTick(() => {
        isMounted.value = true;
        innerPropsRef.value?.rowKey instanceof Function &&
          location.host.includes('test') &&
          alert('rowKey is function');
      });
    }

    if (!isInner && innerPropsRef.value.searchCacheKey) {
      const cache = JSON.parse(sessionStorage.getItem(innerPropsRef.value.searchCacheKey) || '{}');
      Object.keys(cache).length && setPagination({ current: cache.page, pageSize: cache.size });
    }
  }

  const tableAction: TableActionType = {
    reload,
    getSelectRows,
    setSelectedRows,
    clearSelectedRowKeys,
    getSelectRowKeys,
    deleteSelectRowByKey,
    setPagination,
    setTableData,
    updateTableDataRecord,
    deleteTableDataRecord,
    insertTableDataRecord,
    findTableDataRecord,
    redoHeight,
    setSelectedRowKeys,
    setColumns,
    setLoading,
    validateTableData: async (dataIndexList?: BasicColumn['dataIndex'][]) =>
      await runValidateFns(getDataSource(), dataIndexList),
    getDataSource,
    getRawDataSource,
    getSearchInfo,
    setProps,
    getRowSelection,
    getPaginationRef: getPagination,
    getColumns,
    getCacheColumns,
    emit,
    updateTableData,
    setShowPagination,
    getShowPagination,
    toggleActionColumn,
    setCacheColumnsByField,
    expandAll,
    collapseAll,
    expandRows,
    collapseRows,
    scrollTo,
    getProps: () => unref(getBindValues),
    getSize: () => {
      return unref(getBindValues).size as SizeType;
    },
    setCacheColumns,
  };
  createTableContext({ ...tableAction, wrapRef, getBindValues });

  emit('register', tableAction, formActions);

  defineExpose({ wrapRef, tableElRef, ...tableAction, getForm: () => formRef.value });
</script>
<style lang="less">
  @border-color: #cecece4d;

  @prefix-cls: ~'@{namespace}-basic-table';

  [data-theme='dark'] {
    .ant-table-tbody > tr:hover.ant-table-row-selected > td,
    .ant-table-tbody > tr.ant-table-row-selected td {
      background-color: #262626;
    }
  }

  .@{prefix-cls} {
    max-width: 100%;
    height: 100%;

    &-row__striped {
      td {
        background-color: @app-content-background !important;
      }
    }

    &-form-container {
      padding: 0;

      .ant-form {
        width: 100%;
        // margin-bottom: 8px;
        border-radius: @border-radius;
        background-color: @component-background;
      }
    }

    .ant-table-cell {
      overflow: hidden;

      .ant-tag {
        margin-right: 0;
      }
    }

    .ant-table-wrapper {
      padding: 6px 16px;
      border-radius: @border-radius;
      background-color: @component-background;

      .ant-table .ant-table-title {
        padding: 4px 0 8px !important;

        & + .ant-table-container {
          border-top-left-radius: @border-radius;
          border-top-right-radius: @border-radius;
        }
      }

      .ant-table-thead > tr > th {
        background-color: #f6f7f8;
        font-weight: 400;
      }

      .ant-table.ant-table-bordered .ant-table-title {
        border: none !important;
      }

      .ant-table-bordered.ant-table-empty
        .ant-table-container
        .ant-table-body
        .ant-table-tbody
        > .ant-table-placeholder {
        > td {
          border-bottom: 0;
        }

        .ant-table-expanded-row-fixed::after {
          border-right: 0;
        }
      }

      .ant-table-ping-right:not(.ant-table-has-fix-right) .ant-table-container::after {
        width: 9px;
        // border: 1px solid #dfe5ec;
        // border-bottom: 0;
        // border-left: 0;
        border-top-right-radius: @border-radius;
      }

      .ant-table-tbody,
      .virtual-table {
        tr.table-row-active > td.ant-table-cell {
          background-color: #edfdf9;
        }

        .table-row-highlight > .ant-table-cell {
          background-color: #fff7e5 !important;
        }

        .table-cell-warn {
          background-color: #fff7e5 !important;
          color: #ff752b;
        }

        .table-cell-error {
          background-color: #fcdbd9 !important;
          color: #f66362;
        }

        .table-cell-error-text {
          color: #f66362;
        }

        .table-cell-green-text {
          color: #04a584;
        }
      }
    }

    .ant-table {
      width: 100%;
      overflow-x: hidden;

      &-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 6px;
        border-bottom: none;
      }

      .ant-table-tbody > tr.ant-table-row-selected > td,
      .ant-table-tbody > tr.ant-table-row-selected:hover > td {
        background-color: #ecfcf8;
      }

      &.ant-table-bordered > .ant-table-container {
        border-right: 1px solid #dfe5ec;
        border-bottom: 1px solid #dfe5ec;
        border-radius: @border-radius;

        > .ant-table-header .ant-table-thead > tr > .ant-table-cell-scrollbar {
          position: sticky;
          right: 0;
          border-color: #f6f7f8;
        }
      }
    }

    .ant-table-wrapper .ant-table-pagination.ant-pagination {
      margin: 8px 0;
    }

    .ant-table-footer {
      padding: 0;

      .ant-table-wrapper {
        padding: 0;
      }

      table {
        border: none !important;
      }

      .ant-table-body {
        overflow-x: hidden !important;
        //  overflow-y: scroll !important;
      }

      td {
        padding: 12px 8px;
      }
    }

    .ant-pagination {
      .ant-pagination-disabled {
        display: none;
      }

      .ant-pagination-options .ant-select-single {
        width: unset;
      }
    }

    &--minimal {
      .ant-form-item .ant-form-item-label > label {
        font-size: 0.95em;
      }

      .ant-table-wrapper .ant-table {
        font-size: 0.95em;
        line-height: 1.2;

        .ant-table-title,
        .ant-table-footer,
        .ant-table-thead > tr > th,
        .ant-table-tbody > tr > td {
          padding: 2px;
        }
      }
    }

    &--inset {
      > .ant-form,
      > div > .ant-form {
        padding: 0;
        padding-bottom: 12px;
      }

      > .ant-table-wrapper {
        padding: 0 !important;

        .ant-table-pagination.ant-pagination {
          margin-bottom: 0;
        }
      }
    }
  }

  .mts-APP {
    .@{prefix-cls} .ant-table-tbody .ant-table-expanded-row .@{prefix-cls} .ant-table-wrapper {
      padding: 6px 0;

      &:only-child .ant-table {
        margin-left: 0;
      }
    }
  }

  .mts-pc {
    .@{prefix-cls}-form-container {
      .ant-form {
        border-radius: 0;
      }

      .ant-table-wrapper {
        border-radius: 0 0 4px 4px;
      }
    }
  }

  .mts-pad .@{prefix-cls}.search-in-panel {
    padding: 8px;

    .ant-form {
      margin-bottom: 8px;
      padding: 12px 16px;
    }

    .ant-table-wrapper {
      padding-top: 12px;
      padding-bottom: 12px;
    }
  }

  .table-diff-cell .diff {
    background-color: #fff0f0;
    color: @error-color;

    &:first-of-type {
      padding-left: 4px;
    }

    &:last-of-type {
      padding-right: 4px;
    }
  }
</style>
